/**
 * Column profiling — computes per-column statistics from in-memory result rows.
 * Used by both Query Editor and Table Explorer grids.
 */

import type { QueryColumn } from '../../../../types/queryExecution';

const DISTINCT_CAP = 10_000;

const NUMERIC_DATA_TYPES = new Set([
  'int', 'integer', 'smallint', 'tinyint', 'bigint',
  'float', 'real', 'double', 'double precision',
  'decimal', 'numeric', 'money', 'smallmoney',
  'number', // Oracle
]);

export type ColumnProfileStats = {
  columnId: string;
  totalCount: number;
  nullCount: number;
  nullPercent: number;
  distinctCount: number | '10000+';
  min: unknown | undefined;
  max: unknown | undefined;
  avg: number | undefined;
  dataType: string | undefined;
  isNumeric: boolean;
};

function isNumericDataType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  // Strip precision/scale like "decimal(18,2)" → "decimal"
  const base = dataType.toLowerCase().replace(/\(.*\)/, '').trim();
  return NUMERIC_DATA_TYPES.has(base);
}

function tryParseNumber(value: unknown): number | null {
  if (typeof value === 'number') return isNaN(value) ? null : value;
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    const n = Number(value);
    return isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Compute per-column profile statistics from result rows.
 * Single-pass algorithm for performance with large datasets.
 */
export function computeColumnProfiles(
  columns: QueryColumn[],
  rows: Record<string, unknown>[],
): ColumnProfileStats[] {
  if (columns.length === 0) return [];

  const colCount = columns.length;
  const rowCount = rows.length;

  // Pre-compute column keys
  const colKeys = columns.map((col) => col.id || col.name);

  // Per-column accumulators
  const nullCounts = new Int32Array(colCount);
  const numericCounts = new Int32Array(colCount);
  const nonNullCounts = new Int32Array(colCount);
  const sums = new Float64Array(colCount);
  const mins: unknown[] = new Array(colCount).fill(undefined);
  const maxs: unknown[] = new Array(colCount).fill(undefined);
  const distinctSets: Set<unknown>[] = Array.from({ length: colCount }, () => new Set());
  const distinctCapped = new Uint8Array(colCount); // 1 = capped
  const hasHintedNumeric = columns.map((col) => isNumericDataType(col.dataType));

  // Single pass through all rows
  for (let r = 0; r < rowCount; r++) {
    const row = rows[r];
    for (let c = 0; c < colCount; c++) {
      const value = row[colKeys[c]];

      // Null / undefined check
      if (value === null || value === undefined) {
        nullCounts[c]++;
        continue;
      }

      nonNullCounts[c]++;

      // Distinct tracking (with cap)
      if (!distinctCapped[c]) {
        distinctSets[c].add(value);
        if (distinctSets[c].size > DISTINCT_CAP) {
          distinctCapped[c] = 1;
        }
      }

      // Numeric parsing
      const num = tryParseNumber(value);
      if (num !== null) {
        numericCounts[c]++;
        sums[c] += num;

        // Numeric min/max
        if (mins[c] === undefined || num < (mins[c] as number)) {
          mins[c] = num;
        }
        if (maxs[c] === undefined || num > (maxs[c] as number)) {
          maxs[c] = num;
        }
      } else {
        // String/other min/max — compare as strings
        const strVal = String(value);
        if (mins[c] === undefined || strVal < String(mins[c])) {
          mins[c] = strVal;
        }
        if (maxs[c] === undefined || strVal > String(maxs[c])) {
          maxs[c] = strVal;
        }
      }
    }
  }

  // Build results
  return columns.map((col, c) => {
    const totalCount = rowCount;
    const nullCount = nullCounts[c];
    const nonNull = nonNullCounts[c];
    const allNumeric = nonNull > 0 && numericCounts[c] === nonNull;
    const isNumeric = hasHintedNumeric[c] || allNumeric;

    // For non-numeric columns, recalculate min/max as string comparison
    // if we had a mix of numeric-parseable and non-parseable values
    let minVal = mins[c];
    let maxVal = maxs[c];

    if (!isNumeric && nonNull > 0 && numericCounts[c] > 0 && numericCounts[c] < nonNull) {
      // Mixed: rebuild min/max as string comparison
      minVal = undefined;
      maxVal = undefined;
      for (let r = 0; r < rowCount; r++) {
        const value = rows[r][colKeys[c]];
        if (value === null || value === undefined) continue;
        const strVal = String(value);
        if (minVal === undefined || strVal < String(minVal)) minVal = strVal;
        if (maxVal === undefined || strVal > String(maxVal)) maxVal = strVal;
      }
    }

    // Boolean detection — not numeric
    const isBooleanCol = nonNull > 0 && numericCounts[c] === 0 &&
      distinctSets[c].size <= 2 &&
      [...distinctSets[c]].every((v) => v === true || v === false);

    return {
      columnId: colKeys[c],
      totalCount,
      nullCount,
      nullPercent: totalCount > 0 ? Math.round((nullCount / totalCount) * 100) : 0,
      distinctCount: distinctCapped[c] ? '10000+' as const : distinctSets[c].size,
      min: nonNull > 0 ? minVal : undefined,
      max: nonNull > 0 ? maxVal : undefined,
      avg: isNumeric && !isBooleanCol && nonNull > 0
        ? sums[c] / nonNull
        : undefined,
      dataType: col.dataType,
      isNumeric: isNumeric && !isBooleanCol,
    };
  });
}
